import { ConfirmationDialog, ErpDataTableV2 } from '@/components/custom/erp';
import type { DiaChiNhanHang, DiaChiNhanHangInput } from '@/types/schemas';
import { getDataTableColumns } from './cols-definition';
import { ActionBar } from './components/ActionBar';
import { useCRUD, useForms } from '@/hooks';
import { FormDialog } from './components';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';

const PAGE_SIZE = 20;

export default function DiaChiNhanHangPage() {
  const {
    data: receivingAddresses,
    totalItems,
    currentPage,
    detail,
    fetchDetail,
    handlePageChange,
    addItem,
    updateItem,
    deleteItem,
    refreshData,
    searchData
  } = useCRUD<DiaChiNhanHang, DiaChiNhanHangInput>({
    endpoint: QUERY_KEYS.DIA_CHI_GIAO_NHAN_HANG,
    pageSize: PAGE_SIZE
  });

  const {
    showForm,
    showDelete,
    formMode,
    selectedObj,
    selectedRowIndex,
    isCopyMode,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection
  } = useForms<DiaChiNhanHang>(fetchDetail);

  const { searchState, handleSearchChange, onPageChange, hasFilterParams } = useSearchTable({
    searchData,
    isFiltered: false,
    handlePageChange: handlePageChange
  });

  const displayData = hasFilterParams ? searchState.searchResults : receivingAddresses || [];
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;

  const columns = getDataTableColumns();

  const tables = [
    {
      name: '',
      rows: displayData,
      columns: columns
    }
  ];

  const handleAddReceivingAddress = async (data: DiaChiNhanHangInput) => {
    await addItem(data);
    await refreshData();
  };

  const handleUpdateReceivingAddress = async (data: DiaChiNhanHangInput) => {
    if (selectedObj) {
      await updateItem(selectedObj.uuid, data);
      await refreshData();
    }
  };

  const handleSubmit = async (data: DiaChiNhanHangInput) => {
    try {
      if (formMode === 'add') {
        await handleAddReceivingAddress(data);
      } else if (formMode === 'edit' && selectedObj) {
        await handleUpdateReceivingAddress(data);
      }
      handleCloseForm();
      clearSelection();
    } catch (error) {
      return;
    }
  };

  return (
    <div className='flex h-full min-h-[calc(100vh-120px)] w-full flex-col'>
      <ActionBar
        onAddIconClick={handleAddClick}
        onEditIconClick={handleEditClick}
        onDeleteIconClick={handleDeleteClick}
        onCopyIconClick={handleCopyClick}
        onWatchIconClick={handleViewClick}
      />

      <ErpDataTableV2
        tables={tables}
        onRowClick={handleRowClick}
        selectedRowId={selectedRowIndex || undefined}
        totalItems={resolvedTotalItems}
        currentPage={resolvedCurrentPage}
        onPageChange={onPageChange}
        pageSize={PAGE_SIZE}
        onSearchChange={handleSearchChange}
      />

      {showForm && (
        <FormDialog
          mode={formMode}
          open={showForm}
          onClose={handleCloseForm}
          onAddButtonClick={handleAddClick}
          onEditButtonClick={handleEditClick}
          onCopyButtonClick={handleCopyClick}
          onWatchButtonClick={handleViewClick}
          onDeleteButtonClick={handleDeleteClick}
          onSubmit={handleSubmit}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
        />
      )}

      {showDelete && (
        <ConfirmationDialog
          onClose={handleCloseDelete}
          onConfirm={async () => {
            if (selectedObj) {
              await deleteItem(selectedObj.uuid);
              await refreshData();
              handleCloseDelete();
              clearSelection();
            }
          }}
        />
      )}
    </div>
  );
}
